import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import { prospectsAPI } from '@/lib/api';

const CH_STAGE_LABELS = {
    ch_formulaire_rempli: 'Formulaire rempli', ch_visite_technique: 'Visite technique',
    ch_visite_planifiee: 'Visite planifiée', ch_visite_effectuee: 'Visite effectuée',
    ch_dossier_etude: "Dossier d'étude", ch_etude_presentee: 'Étude présentée',
    ch_devis_envoye: 'Devis envoyé', ch_devis_accepte: 'Devis accepté', ch_devis_signe: 'Devis signé',
    ch_dossier_subvention: 'Dossier subvention', ch_subvention_obtenue: 'Subvention obtenue',
    ch_planification: 'Planification', ch_en_cours: 'Travaux d’optimisation en cours', ch_chantier_en_cours: 'Travaux d’optimisation en cours',
    ch_termine: 'Travaux d’optimisation terminé', ch_chantier_termine: 'Travaux d’optimisation terminé',
    ch_attente_paiement: 'Attente paiement', ch_paiement_effectue: 'Paiement effectué',
    ch_valide: 'Validé', ch_gagne: 'Gagné', ch_perdu: 'Perdu',
};

/**
 * Encart "Dossiers audités" — visible uniquement pour les utilisateurs ayant le rôle Auditeur.
 * Liste les dossiers chantier dont l'utilisateur est l'auditeur désigné (aucune commission).
 */
export default function AuditedDossiersWidget() {
    const { user, isAuditor } = useAuth();
    const navigate = useNavigate();
    const [dossiers, setDossiers] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!isAuditor?.()) return;
        prospectsAPI.getAuditedDossiers()
            .then((r) => setDossiers(r.data?.dossiers || []))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, [isAuditor, user?.id]);

    if (!isAuditor?.()) return null;

    return (
        <Card className="border-teal-200" data-testid="audited-dossiers-widget">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base text-teal-800">
                    <ShieldCheck className="w-5 h-5 text-teal-600" />
                    Dossiers audités
                    <Badge className="bg-teal-100 text-teal-700">{dossiers.length}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {loaded && dossiers.length === 0 ? (
                    <p className="text-sm text-slate-400">Aucun dossier ne vous est assigné en audit pour le moment.</p>
                ) : (
                    <div className="space-y-2">
                        {dossiers.map((d) => {
                            const name = d.company_name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Dossier';
                            return (
                                <button
                                    key={d.id}
                                    onClick={() => navigate(`/prospects/${d.id}?wall=chantier`)}
                                    className="w-full flex items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-100 hover:border-teal-300 hover:bg-teal-50/50 transition-colors text-left"
                                    data-testid={`audited-dossier-${d.id}`}
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-slate-800 truncate">{name}</p>
                                        <p className="text-xs text-slate-500">
                                            {CH_STAGE_LABELS[d.chantier_stage] || d.chantier_stage || '—'}
                                            {d.dossier_vp_name && ` · VP : ${d.dossier_vp_name}`}
                                        </p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
